import { ArrowLeft, Copyright, Mail, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/eh8zi2sq_logo.svg";

const DMCAPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="dmca-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="dmca-nav-logo">
              <img src={LOGO_URL} alt="GospelTube" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="dmca-back-btn"
            >
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 rounded-xl bg-brand/20 text-brand">
              <Copyright size={28} />
            </div>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-white">Copyright & DMCA Policy</h1>
              <p className="text-gray-500 text-sm">Effective Date: January 30, 2026 | Last Updated: January 30, 2026</p>
            </div>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 md:p-12 space-y-8">
              
              <p className="text-gray-400 leading-relaxed">
                GospelTube Inc respects intellectual property rights and expects users to do the same. If you believe content on GospelTube infringes your copyright, you may submit a notice under the Digital Millennium Copyright Act ("DMCA").
              </p>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">1) Designated Copyright Agent</h2>
                <div className="bg-zinc-800/50 rounded-xl p-6 mt-4">
                  <div className="flex items-start gap-4">
                    <Mail size={24} className="text-brand flex-shrink-0 mt-1" />
                    <div className="text-gray-400">
                      <p><strong className="text-white">Email:</strong> <a href="mailto:dmca@gospeltube.tv" className="text-brand hover:underline">dmca@gospeltube.tv</a></p>
                      <p><strong className="text-white">General Contact:</strong> <a href="mailto:info@gospeltube.tv" className="text-brand hover:underline">info@gospeltube.tv</a></p>
                      <p><strong className="text-white">Location:</strong> New York, USA</p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">2) How to Submit a DMCA Takedown Notice</h2>
                <p className="text-gray-400 leading-relaxed">Your notice must include:</p>
                <ol className="list-decimal list-inside text-gray-400 space-y-3 mt-4">
                  <li>Your physical or electronic signature</li>
                  <li>Identification of the copyrighted work claimed to have been infringed</li>
                  <li>Identification of the allegedly infringing material and its location on GospelTube (URL(s) or other specific identifier)</li>
                  <li>Your contact information (address, phone number, and email)</li>
                  <li>A statement that you have a good faith belief the use is not authorized by the copyright owner, its agent, or the law</li>
                  <li>A statement, under penalty of perjury, that the information is accurate and you are the owner or authorized to act on behalf of the owner</li>
                </ol>
                <p className="text-gray-400 leading-relaxed mt-6">
                  Send notices to <a href="mailto:dmca@gospeltube.tv" className="text-brand hover:underline">dmca@gospeltube.tv</a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">3) Counter-Notification</h2>
                <p className="text-gray-400 leading-relaxed">
                  If your content was removed and you believe it was removed due to mistake or misidentification, you may submit a counter-notification including:
                </p>
                <ol className="list-decimal list-inside text-gray-400 space-y-3 mt-4">
                  <li>Your physical or electronic signature</li>
                  <li>Identification of the material removed and the location where it appeared before removal</li>
                  <li>A statement, under penalty of perjury, that you have a good faith belief the material was removed due to mistake or misidentification</li>
                  <li>Your name, address, telephone number, and email address</li>
                  <li>A statement that you consent to the jurisdiction of the Federal District Court for the judicial district in which your address is located (or, if outside the United States, the Federal District Court for the Southern District of New York) and that you will accept service of process from the person who provided the original notice or their agent</li>
                </ol>
                <p className="text-gray-400 leading-relaxed mt-6">
                  Send counter-notices to <a href="mailto:dmca@gospeltube.tv" className="text-brand hover:underline">dmca@gospeltube.tv</a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">4) Repeat Infringers</h2>
                <p className="text-gray-400 leading-relaxed">
                  We may terminate accounts of users who are repeat infringers in appropriate circumstances.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
                  <AlertTriangle size={24} className="text-amber-400" />
                  5) Misrepresentations
                </h2>
                <p className="text-gray-400 leading-relaxed">
                  Submitting false or misleading notices or counter-notices may result in liability. Please ensure submissions are accurate.
                </p>
              </section>

            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} GospelTube Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mt-4 text-sm">
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/content-policy" className="text-gray-400 hover:text-white transition-colors">Content Policy</Link>
            <Link to="/dmca" className="text-brand">Copyright/DMCA</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DMCAPage;
